import React, { useState } from 'react';
import { Chart } from 'primereact/chart';
import { ReportHeader } from '../../header/report-header';
import { CourseFinancialSideBarFilter } from './side-bar-filter/course-financial-side-bar-filter';
import Colors from '../../../../utils/colors';
import { observer } from 'mobx-react-lite';
import 'primereact/resources/themes/lara-light-purple/theme.css';
import './report-course-financial-between-branches.css';
import { CourseFinancialFilterDatamodel } from './side-bar-filter/course-financial-side-bar-filter-datamodel';
import { getCourseFinancialBetweenBranchesOperations } from './operations/get-course-financial-between-branches-operations';
import { CourseFinancialAmongBranchesType } from '../../../../api/types/reports/finanace/course-financial-among-branches.type';

export const ReportCourseFinancialBetweenBranches = observer(() => {
  const [financialData, setFinancialData] = useState<
    CourseFinancialAmongBranchesType[]
  >([]);
  const [loading, setLoading] = useState(false);

  const handleSearch = async (filters: CourseFinancialFilterDatamodel) => {
    await getCourseFinancialBetweenBranchesOperations(
      filters,
      setLoading,
      setFinancialData,
    );
  };

  // Prepare data for the first chart - Hours by branch
  const hoursByBranchData = Array.from(
    new Set(financialData.map((item) => item.branchName)),
  ).map((branch) => {
    const totalHours = financialData
      .filter((item) => item.branchName === branch)
      .reduce((sum, item) => sum + item.totalHours, 0);
    return { branch, totalHours };
  });

  const hoursChartData = {
    labels: hoursByBranchData.map((item) => item.branch),
    datasets: [
      {
        label: 'שעות לימוד',
        data: hoursByBranchData.map((item) => item.totalHours),
        backgroundColor: Colors.approveText2,
        borderWidth: 0,
      },
    ],
  };

  // Prepare data for the second chart - Course expenses by branch
  const expensesByBranchData = Array.from(
    new Set(financialData.map((item) => item.branchName)),
  ).map((branch) => {
    const totalExpenses = financialData
      .filter((item) => item.branchName === branch)
      .reduce((sum, item) => sum + item.totalCourseCost, 0);
    return { branch, totalExpenses };
  });

  const expensesChartData = {
    labels: expensesByBranchData.map((item) => item.branch),
    datasets: [
      {
        label: 'הוצאות קורס',
        data: expensesByBranchData.map((item) => item.totalExpenses),
        backgroundColor: Colors.warningYelloText,
        borderWidth: 0,
      },
    ],
  };

  // Prepare data for the third chart - Average cost per hour by branch
  const avgCostByBranchData = Array.from(
    new Set(financialData.map((item) => item.branchName)),
  ).map((branch) => {
    const branchData = financialData.filter(
      (item) => item.branchName === branch,
    );
    const totalHours = branchData.reduce(
      (sum, item) => sum + item.totalHours,
      0,
    );
    const totalRevenue = branchData.reduce(
      (sum, item) => sum + item.totalCourseCost,
      0,
    );
    const avgCost = totalHours > 0 ? totalRevenue / totalHours : 0;
    return { branch, avgCost };
  });

  const avgCostChartData = {
    labels: avgCostByBranchData.map((item) => item.branch),
    datasets: [
      {
        label: 'עלות ממוצעת לשעה',
        data: avgCostByBranchData.map((item) => item.avgCost),
        backgroundColor: Colors.dangerText2,
        borderWidth: 0,
      },
    ],
  };

  const commonChartOptions = {
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
        },
      },
      x: {
        title: {
          display: true,
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          callback: function (value: string | number): string | string[] {
            const label = this.getLabelForValue(value);
            if (label.length > 10) {
              return label.match(/.{1,10}/g)?.join('\n') || label;
            }
            return label;
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    maintainAspectRatio: false,
  };

  const hoursChartOptions = {
    ...commonChartOptions,
    scales: {
      ...commonChartOptions.scales,
      y: {
        ...commonChartOptions.scales.y,
        title: { text: 'מספר שעות' },
      },
      x: {
        ...commonChartOptions.scales.x,
        title: { text: 'שלוחה' },
      },
    },
  };

  const expensesChartOptions = {
    ...commonChartOptions,
    scales: {
      ...commonChartOptions.scales,
      y: {
        ...commonChartOptions.scales.y,
        title: { text: 'הוצאות (₪)' },
      },
      x: {
        ...commonChartOptions.scales.x,
        title: { text: 'שלוחה' },
      },
    },
  };

  const avgCostChartOptions = {
    ...commonChartOptions,
    scales: {
      ...commonChartOptions.scales,
      y: {
        ...commonChartOptions.scales.y,
        title: { text: 'עלות לשעה (₪)' },
      },
      x: {
        ...commonChartOptions.scales.x,
        title: { text: 'שלוחה' },
      },
    },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <ReportHeader
        title="דו״ח השוואת קורסים פיננסי"
        description="דו״ח זה מציג השוואה פיננסית מקיפה של קורסים בין שלוחות שונות, כולל נתוני שעות לימוד, תעריפים והכנסות."
        metadata={[
          { label: 'מטרת הדו״ח', value: 'השוואה פיננסית בין קורסים' },
          { label: 'תדירות עדכון', value: 'יומי' },
        ]}
        category="financial"
      />

      <div style={{ display: 'flex', gap: '20px' }}>
        <div
          style={{
            height: 'calc(100vh - 230px)',
            minHeight: '100%',
            width: '300px',
            minWidth: '250px',
          }}
        >
          <CourseFinancialSideBarFilter onSearch={handleSearch} />
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 230px)',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ flex: 1, minHeight: '200px', padding: '8px 0' }}>
            <h3 style={{ margin: '0 0 2px 0', fontSize: '14px' }}>
              שעות לימוד לפי קורס ושלוחה
            </h3>
            <div
              style={{
                height: 'calc(100% - 20px)',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <Chart
                type="bar"
                data={hoursChartData}
                options={hoursChartOptions}
              />
            </div>
          </div>
          <div style={{ flex: 1, minHeight: '200px', padding: '8px 0' }}>
            <h3 style={{ margin: '0 0 2px 0', fontSize: '14px' }}>
              הוצאות קורס בין השלוחות
            </h3>
            <div
              style={{
                height: 'calc(100% - 20px)',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <Chart
                type="bar"
                data={expensesChartData}
                options={expensesChartOptions}
              />
            </div>
          </div>
          <div style={{ flex: 1, minHeight: '200px', padding: '8px 0' }}>
            <h3 style={{ margin: '0 0 2px 0', fontSize: '14px' }}>
              עלות ממוצעת לשעה לפי קורס ושלוחה
            </h3>
            <div
              style={{
                height: 'calc(100% - 20px)',
                opacity: loading ? 0.6 : 1,
              }}
            >
              <Chart
                type="bar"
                data={avgCostChartData}
                options={avgCostChartOptions}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
